import { getScheduleStaffColor } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'
import type { SeriesPeopleKeys, SeriesRunVisit } from 'features/Series/api/series.types'

import type { PersonOption } from '../ProviderField'
import { SERIES_TEXT_V2 } from './seriesText'

export type VisitPeople = { people: number[]; primaryId: number }

export const MAX_VISIT_PEOPLE = 10

// Builds a visit's people: deduped, capped, with a primary that is always in the list.
export const peopleOf = (ids: number[], primaryId?: number | null): VisitPeople | null => {
  const people = [...new Set(ids)].slice(0, MAX_VISIT_PEOPLE)
  if (!people.length) return null
  return {
    people,
    primaryId: primaryId != null && people.includes(primaryId) ? primaryId : people[0],
  }
}

export const withPerson = (value: VisitPeople, id: number): VisitPeople =>
  value.people.includes(id) || value.people.length >= MAX_VISIT_PEOPLE
    ? value
    : { ...value, people: [...value.people, id] }

export const withoutPerson = (value: VisitPeople, id: number): VisitPeople | null =>
  peopleOf(
    value.people.filter((person) => person !== id),
    value.primaryId,
  )

export const makePrimary = (value: VisitPeople, id: number): VisitPeople =>
  value.people.includes(id) ? { ...value, primaryId: id } : value

export const samePeople = (a: VisitPeople | null, b: VisitPeople | null): boolean => {
  if (!a || !b) return a === b
  return (
    a.primaryId === b.primaryId &&
    a.people.length === b.people.length &&
    a.people.every((id) => b.people.includes(id))
  )
}

export const peopleKeys = (value: VisitPeople): SeriesPeopleKeys =>
  value.people.length === 1
    ? { doctor_id: value.people[0] }
    : { doctor_ids: value.people, primary_doctor_id: value.primaryId }

// People with a Residen user; the primary is the known flagged one, else the first.
export const visitPeopleOf = (visit: SeriesRunVisit): VisitPeople | null => {
  const ids = visit.people.flatMap(({ doctor }) => (doctor.id === null ? [] : [doctor.id]))
  const primary = visit.people.find((person) => person.isPrimary && person.doctor.id !== null)
  return peopleOf(ids, primary?.doctor.id)
}

export const unknownPeopleOf = (visit: SeriesRunVisit): { names: string[]; primary: boolean } => {
  const unknown = visit.people.filter((person) => person.doctor.id === null)
  return {
    names: unknown.map((person) => person.doctor.name ?? SERIES_TEXT_V2.unknownProvider),
    primary: unknown.some((person) => person.isPrimary),
  }
}

// Adds the run's providers that the site list does not carry.
export const optionsWithRun = (
  options: PersonOption[],
  visits: SeriesRunVisit[],
): PersonOption[] => {
  const seen = new Set(options.map((option) => option.id))
  const extra: PersonOption[] = []
  visits.forEach((visit) =>
    visit.people.forEach(({ doctor }) => {
      if (doctor.id === null || seen.has(doctor.id)) return
      seen.add(doctor.id)
      extra.push({
        id: doctor.id,
        name: doctor.name ?? SERIES_TEXT_V2.unknownProvider,
        role: '',
        color: getScheduleStaffColor(doctor.id),
      })
    }),
  )
  return extra.length ? [...options, ...extra] : options
}
